import { apiFetch } from './http';
import type { MissionView } from './missions';

export interface Sector {
  name: string;
  x: number;
  y: number;
  radius: number;
  control: 'enemy' | 'contested' | 'friendly';
  changedChapter: number;
}

export interface CastMemory {
  id: string;
  name: string;
  role: string;
  disposition: string;
  status: 'alive' | 'dead' | 'captured' | 'escaped';
  history: string[];
}

export interface CampaignThread {
  id: string;
  text: string;
  recipeId: string | null;
  objectiveType: string | null;
  sector: string | null;
  openedChapter: number;
  resolved: boolean;
}

export interface CampaignBoard {
  day: number;
  hour: number;
  enemyStrength: number;
  civilianDisposition: number;
  sectors: Sector[];
  cast: CastMemory[];
  threads: CampaignThread[];
  gear: Record<string, unknown>;
  stats: {
    chapters: number;
    successes: number;
    failures: number;
    casualties: number;
    objectivesDone: number;
    objectivesFailed: number;
  };
}

export interface ChapterDelta {
  outcome: string;
  sectors: { name: string; from: string; to: string }[];
  objectivesFailed: { id: string; title: string; type: string; sector?: string }[];
  objectivesDone: { id: string; title: string; type: string }[];
  enemyStrengthBefore: number;
  enemyStrengthAfter: number;
  civilianDisposition: number;
  cast: { name: string; from: string; to: string }[];
  medals: { name: string; medal: string }[];
  threadsOpen: string[];
}

export interface ChapterView {
  ordinal: number;
  missionCode: string | null;
  missionTitle: string | null;
  sessionCode: string | null;
  sessionState: string | null;
  outcome: string | null;
  delta: ChapterDelta | null;
  createdAt: string;
  playedAt: string | null;
}

export interface StandingView {
  /** A stable hash of the player, never the Steam64. */
  playerId: string;
  name: string;
  attended: number;
  survived: number;
  medals: string[];
  /** Cosmetic, from attendance alone: recluta, soldat, caporal, sergent, tinent. */
  rank: string;
}

export interface FinalReport {
  title: string;
  chapters: number;
  successes: number;
  failures: number;
  casualties: number;
  sectorsHeld: number;
  sectorsTotal: number;
  verdict: 'won' | 'held' | 'lost';
  chapterList: { ordinal: number; title?: string; outcome: string | null }[];
  standings: { name: string; attended: number; survived: number; medals: string[] }[];
}

export interface CampaignView {
  code: string;
  title: string;
  ownerId: number | null;
  visibility: 'PRIVATE' | 'UNLISTED' | 'PUBLIC';
  state: 'ACTIVE' | 'FINISHED';
  worldName: string;
  /** The world's readable name; the class name when the platform has nothing better. */
  worldDisplayName?: string | null;
  modSetHash: string | null;
  /** The server the campaign is played on: each chapter is made with what it runs that day. */
  panelServerId: number | null;
  playerSide: string;
  playerFaction: string;
  enemyProfileId: number | null;
  players: number;
  nextDecidedBy: 'creator' | 'vote';
  gearAllow: string[] | null;
  chapter: number;
  canEdit: boolean;
  board: CampaignBoard | null;
  chapters: ChapterView[];
  standings: StandingView[];
  suggestions: string[];
  finalReport: FinalReport | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignRequest {
  title: string;
  worldClass: string;
  modSetHash: string | null;
  /** The server the campaign is played on: each chapter is made with what it runs that day. */
  panelServerId: number | null;
  playerSide: string;
  playerFaction: string | null;
  enemyProfileId: number | null;
  players: number;
  nextDecidedBy: 'creator' | 'vote';
  gearAllow: string[] | null;
  seed: number | null;
}

export const fetchCampaigns = () => apiFetch<CampaignView[]>('/api/campaigns');
export const fetchCampaign = (code: string) => apiFetch<CampaignView>(`/api/campaigns/${code}`);
export const createCampaign = (request: CreateCampaignRequest) =>
  apiFetch<CampaignView>('/api/campaigns', { method: 'POST', body: request });
export const updateCampaign = (
  code: string,
  settings: { title?: string; nextDecidedBy?: string; gearAllow?: string[]; players?: number },
) => apiFetch<CampaignView>(`/api/campaigns/${code}`, { method: 'PUT', body: settings });
export const nextChapter = (code: string, recipeId: string | null) =>
  apiFetch<MissionView>(`/api/campaigns/${code}/next`, { method: 'POST', body: { recipeId } });
export const finishCampaign = (code: string) =>
  apiFetch<CampaignView>(`/api/campaigns/${code}/finish`, { method: 'POST' });
export const copyCampaign = (code: string) =>
  apiFetch<CampaignView>(`/api/campaigns/${code}/copy`, { method: 'POST' });
export const setCampaignVisibility = (code: string, visibility: CampaignView['visibility']) =>
  apiFetch<CampaignView>(`/api/campaigns/${code}/visibility`, {
    method: 'POST',
    body: { visibility },
  });
